import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, ConfirmModalRef, usePageNavigation } from '@shared/components';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { usePopupAdmin } from '../../hooks/usePopupAdmin';
import { usePopupCountByStatus } from '../../hooks/usePopupCountByStatus';
import type { Popup, PopupCountFilters, PopupFilters, PopupSearchFilter, PopupStatus } from '../../models/Popup';
import { excludeStatusFromSearchFilter } from '../../models/Popup';
import { PopupsView } from '../display/PopupsView';
import { PopupModal } from '../display/PopupModal';
import { PopupEventsModal, type PopupEventsModalRef } from '../modals/PopupEventsModal';

const URL_SYNC_DEBOUNCE_MS = 500;

function buildStatusFilter(status: PopupStatus): PopupSearchFilter {
  return {
    filters: [
      {
        column: 'status',
        operator: 'in',
        value: [status],
      },
    ],
    logic: 'AND',
  };
}

function buildCountFilters(search: string, searchFilter?: PopupSearchFilter): PopupCountFilters {
  return {
    search: search.trim() || undefined,
    searchFilter: excludeStatusFromSearchFilter(searchFilter),
  };
}

export const PopupAdminMain = () => {
  const { t } = useTranslation('popups');
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const eventsModalRef = useRef<PopupEventsModalRef>(null);

  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    status: { type: 'string', default: 'active' },
  });

  const [searchTerm, setSearchTerm] = useState(() => queryParams.search ?? '');
  const [statusTab, setStatusTab] = useState<PopupStatus>(() =>
    queryParams.status === 'archived' ? 'archived' : 'active',
  );
  const [previewPopup, setPreviewPopup] = useState<Popup | null>(null);
  const deferredSearch = useDeferredValue(searchTerm);
  const urlSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filters = useMemo<PopupFilters>(
    () => ({
      page: 1,
      perPage: 50,
      search: deferredSearch.trim() || undefined,
      searchFilter: buildStatusFilter(statusTab),
    }),
    [deferredSearch, statusTab],
  );

  const countFilters = useMemo(() => buildCountFilters(deferredSearch), [deferredSearch]);
  const hasSearch = deferredSearch.trim().length > 0;
  const globalCountFilters = useMemo(() => buildCountFilters(''), []);

  const { popups, meta, isLoading, isFetching, refetch, archivePopupAsync, deletePopupAsync, updatePopupAsync } =
    usePopupAdmin(filters);

  const { counts: statusCounts, isFetching: isFetchingCounts } = usePopupCountByStatus(countFilters);
  const { counts: globalCounts } = usePopupCountByStatus(globalCountFilters, { enabled: hasSearch });

  const hasAnyPopups = hasSearch
    ? globalCounts.active + globalCounts.archived > 0
    : statusCounts.active + statusCounts.archived > 0;

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleStatusTabChange = useCallback((tab: PopupStatus) => {
    setStatusTab(tab);
  }, []);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (urlSyncTimerRef.current) {
      clearTimeout(urlSyncTimerRef.current);
    }

    urlSyncTimerRef.current = setTimeout(() => {
      const urlSearch = queryParams.search ?? '';
      const urlStatus = queryParams.status === 'archived' ? 'archived' : 'active';

      if (urlSearch !== searchTerm || urlStatus !== statusTab) {
        updateQueryParams({ search: searchTerm, status: statusTab });
      }
    }, URL_SYNC_DEBOUNCE_MS);

    return () => {
      if (urlSyncTimerRef.current) {
        clearTimeout(urlSyncTimerRef.current);
      }
    };
  }, [searchTerm, statusTab, queryParams.search, queryParams.status, updateQueryParams]);

  const handlePreview = useCallback((popup: Popup) => {
    setPreviewPopup(popup);
  }, []);

  const handleViewEvents = useCallback((popup: Popup) => {
    eventsModalRef.current?.open(popup);
  }, []);

  const handleArchive = useCallback(
    (popup: Popup) => {
      confirmModalRef.current?.open(
        () => archivePopupAsync(popup.id),
        undefined,
        t('modal.archive.title'),
        t('modal.archive.message'),
        t('modal.archive.confirm'),
        t('modal.archive.cancel'),
      );
    },
    [archivePopupAsync, t],
  );

  const handleRestore = useCallback(
    (popup: Popup) => {
      updatePopupAsync({ id: popup.id, data: { status: 'active' } });
    },
    [updatePopupAsync],
  );

  const handleDelete = useCallback(
    (popup: Popup) => {
      confirmModalRef.current?.open(
        () => deletePopupAsync(popup.id),
        undefined,
        t('modal.delete.title'),
        t('modal.delete.message'),
        t('modal.delete.confirm'),
        t('modal.delete.cancel'),
      );
    },
    [deletePopupAsync, t],
  );

  const breadcrumbs = useMemo(() => [{ label: t('pageTitle') }], [t]);

  usePageNavigation({ breadcrumbs });

  return (
    <>
      <ConfirmModal ref={confirmModalRef} />
      <PopupEventsModal ref={eventsModalRef} />

      {previewPopup ? (
        <PopupModal
          popup={previewPopup}
          isVisible
          currentIndex={1}
          totalCount={1}
          onNext={() => setPreviewPopup(null)}
        />
      ) : null}

      <PopupsView
        popups={popups}
        totalCount={meta?.total ?? 0}
        hasAnyPopups={hasAnyPopups}
        statusTab={statusTab}
        statusCounts={statusCounts}
        isLoadingCounts={isFetchingCounts}
        searchTerm={searchTerm}
        isFetching={isFetching}
        isLoading={isLoading}
        isFiltering={searchTerm !== deferredSearch}
        onStatusTabChange={handleStatusTabChange}
        onSearchChange={handleSearchChange}
        onRefetch={handleRefetch}
        onPreview={handlePreview}
        onViewEvents={handleViewEvents}
        onArchive={handleArchive}
        onRestore={handleRestore}
        onDelete={handleDelete}
      />
    </>
  );
};
